/**
 * SecureTokenManager - Frontend service for secure token handling
 *
 * Manages in-memory access tokens, CSRF tokens from cookies, and
 * refresh token rotation via httpOnly cookies.
 *
 * The refresh token is never accessible to JavaScript — it lives in an
 * httpOnly cookie managed by the browser. The access token is stored
 * only in memory (never localStorage or sessionStorage).
 *
 * ## Cross-Tab Coordination (TASK-009)
 *
 * This manager operates independently per tab — there is no cross-tab
 * token synchronization via BroadcastChannel or similar APIs. This is
 * an intentional design choice:
 *
 * - The server-side refresh endpoint applies a 30-second grace period
 *   (TASK-003) that accepts the previous refresh token after rotation.
 *   This handles the case where multiple tabs refresh concurrently.
 * - BroadcastChannel-based coordination would require leader election,
 *   message ordering guarantees, and error recovery for stale/closed
 *   channels — significant complexity for a scenario already mitigated
 *   server-side.
 * - If the 30-second grace period proves insufficient under real-world
 *   usage, BroadcastChannel coordination can be added as a future
 *   enhancement without changing the public API.
 *
 * (Monday Item ID: 11501542831)
 */

import { apiClient } from '@/lib/api/client';

// Token refresh threshold - refresh when less than this many ms remain
const REFRESH_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

interface TokenPayload {
  sub: string;
  exp: number;
  iat: number;
  email?: string;
  username?: string;
  roles?: string[];
  is_superuser?: boolean;
  type: 'access';
}

interface RefreshResponse {
  access_token: string;
  token_type?: string;
  expires_in: number;
}

type TokenRefreshCallback = (newToken: string) => void;

/**
 * Decode a JWT token without verification (client-side).
 * Only use for reading expiry — never trust for authentication.
 */
function decodeToken(token: string): TokenPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

/**
 * SecureTokenManager handles token lifecycle on the frontend.
 *
 * Access token: in-memory only (this.accessToken).
 * Refresh token: httpOnly cookie (browser-managed, not accessible here).
 * CSRF token: readable cookie (used for double-submit validation).
 */
class SecureTokenManager {
  /** In-memory only, scoped to this tab. Not shared across tabs. */
  private accessToken: string | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private refreshCallbacks: Set<TokenRefreshCallback> = new Set();
  private isRefreshing = false;
  private refreshPromise: Promise<string> | null = null;

  /**
   * Initialize the token manager by attempting a silent refresh.
   *
   * Returns true if a valid session exists (refresh cookie is present
   * and the server returned a new access token), false otherwise.
   */
  async initialize(): Promise<boolean> {
    try {
      await this.refreshAccessToken();
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Store initial tokens received from the login response.
   * Called by the auth store after a successful login.
   */
  setInitialTokens(accessToken: string): void {
    this.setAccessToken(accessToken);
  }

  /**
   * Set the access token in memory and schedule refresh.
   */
  setAccessToken(token: string): void {
    this.accessToken = token;

    // Schedule refresh before expiry
    this.scheduleRefresh(token);

    // Notify subscribers
    this.refreshCallbacks.forEach((cb) => cb(token));
  }

  /**
   * Get the current access token (in-memory only).
   */
  getAccessToken(): string | null {
    return this.accessToken;
  }

  /**
   * Get the CSRF token for request headers.
   * Always reads directly from the cookie to avoid stale cached values
   * after idle periods when the cookie may have been updated by a refresh.
   */
  getCsrfToken(): string | null {
    return this.getCsrfTokenFromCookie();
  }

  /**
   * Subscribe to token refresh events.
   */
  onTokenRefresh(callback: TokenRefreshCallback): () => void {
    this.refreshCallbacks.add(callback);
    return () => this.refreshCallbacks.delete(callback);
  }

  /**
   * Schedule automatic token refresh before expiry.
   *
   * This timer is scoped to the current tab. Each tab independently
   * schedules its own refresh cycle. The server-side 30-second grace
   * period ensures concurrent refreshes from multiple tabs do not
   * invalidate each other's sessions.
   */
  private scheduleRefresh(token: string): void {
    // Clear any existing timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    const payload = decodeToken(token);
    if (!payload?.exp) return;

    const expiresAt = payload.exp * 1000; // Convert to ms
    const now = Date.now();
    const timeUntilExpiry = expiresAt - now;

    // Schedule refresh before expiry threshold
    const refreshIn = Math.max(0, timeUntilExpiry - REFRESH_THRESHOLD_MS);

    if (refreshIn > 0) {
      this.refreshTimer = setTimeout(() => {
        this.refreshAccessToken().catch(console.error);
      }, refreshIn);
    } else if (timeUntilExpiry > 0) {
      // Token expires soon but not yet - refresh immediately
      this.refreshAccessToken().catch(console.error);
    }
    // If already expired, don't schedule - let the next request handle it
  }

  /**
   * Refresh the access token using the httpOnly refresh cookie.
   * Deduplicates concurrent refresh requests within this tab.
   * Cross-tab deduplication is handled server-side via the grace period.
   */
  async refreshAccessToken(): Promise<string> {
    // Deduplicate concurrent refresh requests
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    this.isRefreshing = true;
    this.refreshPromise = this.doRefresh();

    try {
      return await this.refreshPromise;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  /**
   * Perform the actual token refresh.
   */
  private async doRefresh(): Promise<string> {
    try {
      const response = await apiClient.post<RefreshResponse>(
        '/auth/refresh',
        {},
        {
          withCredentials: true, // Send httpOnly refresh token cookie
          headers: { 'X-CSRF-Token': this.getCsrfToken() || '' },
        }
      );

      const { access_token } = response.data;
      this.setAccessToken(access_token);

      return access_token;
    } catch (error) {
      // Refresh failed - clear in-memory state
      this.accessToken = null;
      if (this.refreshTimer) {
        clearTimeout(this.refreshTimer);
        this.refreshTimer = null;
      }
      throw error;
    }
  }

  /**
   * Check if the current token is expired or about to expire.
   */
  isTokenExpired(): boolean {
    if (!this.accessToken) return true;

    const payload = decodeToken(this.accessToken);
    if (!payload?.exp) return true;

    const expiresAt = payload.exp * 1000;
    const now = Date.now();

    return now >= expiresAt - REFRESH_THRESHOLD_MS;
  }

  /**
   * Get a valid token, refreshing if necessary.
   */
  async getValidToken(): Promise<string | null> {
    if (!this.accessToken) return null;

    if (this.isTokenExpired()) {
      try {
        return await this.refreshAccessToken();
      } catch {
        return null;
      }
    }

    return this.accessToken;
  }

  /**
   * Clear all tokens (logout).
   * Calls the logout endpoint to revoke the refresh cookie server-side,
   * then clears in-memory state regardless of API call outcome.
   */
  clearTokens(): void {
    // Call logout endpoint (fire-and-forget, wrapped in try/catch)
    apiClient.post('/auth/logout', {}, { withCredentials: true }).catch((error: unknown) => {
      const message = error instanceof Error ? error.message : String(error);
      console.warn('[TokenManager] Logout API call failed:', message);
    });

    this.accessToken = null;

    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /**
   * Extract CSRF token from cookie.
   */
  private getCsrfTokenFromCookie(): string | null {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'csrf_token') {
        return decodeURIComponent(value);
      }
    }
    return null;
  }

  /**
   * Get decoded token payload (for display purposes only).
   */
  getTokenPayload(): TokenPayload | null {
    if (!this.accessToken) return null;
    return decodeToken(this.accessToken);
  }

  /**
   * Get user info from token.
   */
  getUserInfo(): { userId: string; email?: string; roles?: string[] } | null {
    const payload = this.getTokenPayload();
    if (!payload) return null;

    return {
      userId: payload.sub,
      email: payload.email,
      roles: payload.roles,
    };
  }
}

// Export singleton instance (no auto-initialize — App.tsx calls initialize)
export const tokenManager = new SecureTokenManager();

export { SecureTokenManager, decodeToken };
export type { TokenPayload, RefreshResponse, TokenRefreshCallback };
